import React, { useState } from 'react';
import { AlertObject, AlertType } from '../../../../../../../components/Alert';
import { BulkActionButton } from '../BulkActionButton';
import { BulkRejectModal } from './BulkRejectModal';
import { BULK_ACTION_ERROR_MESSAGE } from '..';
import { checkIfSelectedCandidatesHaveSameStatus } from '../utils';
import { NewListCandidate } from '../../../../../../../entities/v1/applicant_tracking/NewListCandidate';
import { ApplicationService } from '../../../../../../../services/applicant_tracking/ApplicationService';
import { useBulkAction } from '../../../../../../../hooks/useBulkAction';

interface PropTypes {
  candidates: NewListCandidate[];
  selectedCandidates: NewListCandidate[];
  bulkSelectedIds: number[] | null;
  selectionCountLabel: string;
  reloadPage: () => void;
  setAlert: (alert: AlertObject) => void;
}

function actionStatusAndTooltipText(selectedCandidates: NewListCandidate[]) {
  const alreadyRejected = selectedCandidates.every(
    (candidate) => candidate.application.status === 'rejected',
  );
  const actionDisabled =
    alreadyRejected ||
    selectedCandidates.length === 0 ||
    !checkIfSelectedCandidatesHaveSameStatus(selectedCandidates);

  const toolTipText = actionDisabled
    ? alreadyRejected
      ? 'All selected candidates have already been rejected'
      : 'Bulk rejection is only available for candidates that share the same status'
    : 'Reject Candidate';

  return { actionDisabled, toolTipText };
}

export function RejectCandidateButton(props: PropTypes) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { progress, execute } = useBulkAction();

  const { actionDisabled, toolTipText } =
    props.bulkSelectedIds !== null
      ? {
          actionDisabled: props.bulkSelectedIds.length === 0,
          toolTipText: 'Reject Candidate',
        }
      : actionStatusAndTooltipText(props.selectedCandidates);

  const applicationIds =
    props.bulkSelectedIds !== null
      ? props.bulkSelectedIds
      : props.selectedCandidates.map((candidate) => candidate.application.id);

  const onBulkReject =
    props.bulkSelectedIds !== null
      ? async (rejectReasonId: number) => {
          const failedCount = await execute(props.bulkSelectedIds, (batchIds) =>
            ApplicationService.bulkReject({
              applicationIds: batchIds,
              rejectReasonId,
            }),
          );
          if (failedCount > 0) {
            throw new Error('Some rejections could not be processed.');
          }
        }
      : undefined;

  const handleModalClose = (succeeded: boolean) => {
    if (succeeded) {
      props.reloadPage();
      props.setAlert({
        message: (
          <span>
            <b>{props.selectionCountLabel}</b> were successfully rejected!
          </span>
        ),
        type: AlertType.Success,
      });
    } else if (succeeded === false) {
      props.setAlert({
        message: BULK_ACTION_ERROR_MESSAGE,
        type: AlertType.Danger,
      });
    }
    setIsModalOpen(false);
  };

  return (
    <>
      <BulkActionButton
        iconName='bi-file-x'
        toolTipText={toolTipText}
        disabled={actionDisabled}
        onClick={() => setIsModalOpen(true)}
        analyticsId='bulk-reject-candidate-button'
      />
      {isModalOpen && (
        <BulkRejectModal
          applicationIds={applicationIds}
          selectionCountLabel={props.selectionCountLabel}
          warningDescription={
            props.bulkSelectedIds !== null
              ? 'These applications will be rejected regardless of their current status. Already-rejected and draft applications will be skipped.'
              : undefined
          }
          progress={progress}
          onBulkReject={onBulkReject}
          onClose={handleModalClose}
        />
      )}
    </>
  );
}
